// components/ArtistSearch.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArtistSearchResult, AssignArtistRequest, Artist } from '../../models/artist';
import '../../styles/components.css';

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";

interface ArtistSearchProps {
    isOpen: boolean;
    onClose: () => void;
    onAssignArtist: (request: AssignArtistRequest) => Promise<void>;
    onArtistSelect: (artist: Artist) => void;
    mode?: 'select' | 'assign'; 
}

const ArtistSearch: React.FC<ArtistSearchProps> = ({
    isOpen,
    onClose,
    onAssignArtist,
    onArtistSelect,
    mode
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ArtistSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedArtist, setSelectedArtist] = useState<ArtistSearchResult | null>(null);
    const [assignNotes, setAssignNotes] = useState('');

    const { activeClientId, getAccessToken, user } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();

    const assignMode = mode == 'assign';

    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const searchReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    // Debounced search
    useEffect(() => {
        if (!isOpen || searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }

        const delayDebounceFn = setTimeout(() => {
            performSearch();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, isOpen]);

    const performSearch = async () => {
        if (clientIdRef.current == null) return;
        if (searchQuery.length < 2) return;

        try {
            const tasks = [
                searchReq.run({ method: "GET", url: `/artists/search?query=${encodeURIComponent(searchQuery)}&clientId=${clientIdRef.current}` }),
            ];

            const [s] = await Promise.allSettled(tasks);

            if (s.status === "fulfilled") setSearchResults(s.value);
        }
        catch (error) {
            console.error('Failed to load artists:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectArtist = (artist: Artist) => {
        onArtistSelect(artist); 
        onClose();
    };

    const handleAssignArtist = async (artist: ArtistSearchResult) => {
        try {
            await onAssignArtist({
                artistId: artist.artistId,
                clientNotes: assignNotes,
                clientBiography: assignNotes
            });

            if (!assignMode) {
                handleSelectArtist(artist);
            }

            setSelectedArtist(null);
            setAssignNotes('');
            onClose();
        } catch (error) {
            console.error('Failed to assign artist:', error);
        }
    };

    const handleQuickAssign = async (artist: ArtistSearchResult) => {
        try {
            await onAssignArtist({
                artistId: artist.artistId,
                clientNotes: '',
                clientBiography: ''
            });
            // Update local state to show as assigned
            setSearchResults(prev =>
                prev.map(a =>
                    a.artistId === artist.artistId
                        ? { ...a, isAlreadyAssigned: true }
                        : a
                )
            );

            if (!assignMode) {
                handleSelectArtist(artist);
            }

        } catch (error) {
            console.error('Failed to assign artist:', error);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[70vh] flex flex-col border border-gray-300">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold">Search Artists</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Search Input */}
                <div className="p-6 border-b">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search for artists by name or biography..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {isSearching && (
                            <div className="absolute right-3 top-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Search Results */}
                <div className="search-results-container flex-1">
                    {searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                        <div className="p-6 text-center text-gray-500">
                            No artists found matching "{searchQuery}"
                        </div>
                    )}

                    {searchResults.map(artist => (
                        <div
                            key={artist.artistId}
                            className="border-b border-gray-200 hover:bg-gray-50"
                        >
                            <div className="p-4 flex justify-between items-center">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                        <h3 className="font-medium text-gray-900">
                                            {artist.artistName}
                                        </h3>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${artist.source === 1
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-green-100 text-green-800'
                                            }`}>
                                            {artist.source === 1 ? 'System' : 'User'}
                                        </span>
                                        {artist.isAlreadyAssigned && artist.source == 1 && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                Assigned
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-1 text-sm text-gray-500">
                                        {artist.nationality && <span>{artist?.nationality?.split(',')[0].trim()}</span>}
                                        {artist.artistPeriod && (
                                            <span>
                                                {artist?.nationality?.split(',')[0].trim() && ' • '}
                                                Artist Dates: {artist.artistPeriod}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex space-x-2 ml-4">
                                    {!artist.isAlreadyAssigned ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation(); 
                                                    handleQuickAssign(artist);
                                                }}
                                                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                            >
                                                Quick Assign
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation(); 
                                                    setSelectedArtist(artist);
                                                }}
                                                className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                            >
                                                Assign with Bio
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {assignMode ? (
                                                <span className="px-3 py-1 text-sm text-gray-500">
                                                    Already Assigned
                                                </span>
                                             ) : (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectArtist(artist);
                                                        }}
                                                            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                                    >
                                                        Select Artist
                                                    </button>
                                                )
                                            }
                                        </>    
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Assign with Custom Biography Modal */}
                {selectedArtist && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="p-6 border-b">
                                <h3 className="text-lg font-medium">Assign {selectedArtist.artistName}</h3>
                            </div>

                            <div className="p-6 space-y-4">
                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                        Custom Biography (Optional)
                                    </label>
                                    <textarea
                                        id="notes"
                                        rows={3}
                                        value={assignNotes}
                                        onChange={(e) => setAssignNotes(e.target.value)}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Add your own additional bio/notes for this artist..."
                                    />
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedArtist(null);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-white-700 hover:text-gray-900"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAssignArtist(selectedArtist);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        Assign Artist
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArtistSearch;